// Settings card: record a 60 s stroke-sensor log and download it as a file.
import { useEffect, useRef, useState } from 'react';
import { Activity, Download, Square } from 'lucide-react';
import { toast } from 'sonner';
import { createStrokeSensorRecorder, downloadLog, getActiveSensorLog } from '@/lib/strokeSensorLogger';

const LABELS = ['desk still', 'moving by hand', 'still on water', 'turning', 'paddling', '18 spm', '24 spm', '32 spm'];

async function askMotionPermission() {
  const DME = window.DeviceMotionEvent;
  if (DME && typeof DME.requestPermission === 'function') return (await DME.requestPermission()) === 'granted';
  return !!DME;
}

export const StrokeSensorLogger = () => {
  const [label, setLabel] = useState(LABELS[0]);
  const [running, setRunning] = useState(() => !!getActiveSensorLog());
  const [tick, setTick] = useState(null);
  const [lastLog, setLastLog] = useState(null);
  const recRef = useRef(getActiveSensorLog());
  useEffect(() => {
    const onState = (e) => {
      if (e.detail?.recording) return;
      setRunning(false);
      if (e.detail?.log) setLastLog(e.detail.log);
    };
    const onTick = (e) => setTick(e.detail);
    window.addEventListener('crewz-sensor-log-state', onState);
    window.addEventListener('crewz-sensor-log-tick', onTick);
    return () => {
      window.removeEventListener('crewz-sensor-log-state', onState);
      window.removeEventListener('crewz-sensor-log-tick', onTick);
    };
  }, []);

  const start = async () => {
    if (!(await askMotionPermission())) { toast.error('Motion sensor permission is needed'); return; }
    setLastLog(null); setTick(null); setRunning(true);
    recRef.current = createStrokeSensorRecorder({
      label,
      durationS: 60,
      onDone: (log) => { downloadLog(log); toast.success('Log saved — attach the file in chat'); },
    });
  };

  const s = lastLog?.summary;
  return (
    <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg px-4 py-3" data-testid="stroke-sensor-logger">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-4 h-4 text-zinc-500" />
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Stroke sensor log (60 s)</span>
      </div>
      <p className="text-xs text-zinc-400 mb-3">Records motion and GPS for 60 s and downloads a file. While it records, the cockpit shows a 'rowing detected' light.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <select value={label} onChange={(e) => setLabel(e.target.value)} disabled={running} className="text-xs border border-zinc-200 dark:border-zinc-600 rounded-md px-2 py-1.5 bg-zinc-50 dark:bg-zinc-700 dark:text-white" data-testid="stroke-log-label">
          {LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        {!running ? (
          <button type="button" onClick={start} className="px-3 py-1.5 text-xs font-bold rounded-md bg-zinc-900 text-white hover:bg-zinc-700 transition-colors" data-testid="stroke-log-start">Start 60 s log</button>
        ) : (
          <button type="button" onClick={() => recRef.current?.stop()} className="px-3 py-1.5 text-xs font-bold rounded-md bg-red-600 text-white flex items-center gap-1 transition-colors" data-testid="stroke-log-stop"><Square className="w-3 h-3" /> Stop ({Math.round(tick?.elapsed || 0)} s)</button>
        )}
        {lastLog && (
          <button type="button" onClick={() => downloadLog(lastLog)} className="px-3 py-1.5 text-xs font-bold rounded-md border border-zinc-300 flex items-center gap-1" data-testid="stroke-log-download"><Download className="w-3 h-3" /> Download again</button>
        )}
      </div>
      {running && tick?.last && (
        <p className="mt-2 text-[11px] font-mono text-zinc-500" data-testid="stroke-log-live">
          mag {tick.last.avgMagnitude} · AC {tick.last.acFirstPeak ?? '–'} · p2p {tick.last.peakToPeak4s} · GPS {tick.last.gpsSpeed ?? '–'} m/s · {tick.last.source}
        </p>
      )}
      {s && (
        <p className="mt-2 text-[11px] font-mono text-zinc-500" data-testid="stroke-log-summary">
          avg mag {s.avgMagnitude?.avg} · AC peak max {s.acFirstPeak?.max ?? '–'} · p2p max {s.peakToPeak4s?.max} · GPS avg {s.gpsSpeed?.avg ?? '–'} · source {s.accelerationSource.acceleration ? 'without gravity' : 'with gravity'}
        </p>
      )}
    </div>
  );
};
